import React, { useState } from 'react';
import { AdminPageLayout } from '@/components/admin/shared/AdminPageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Settings, 
  Shield, 
  Bell, 
  Link, 
  Save,
  CheckCircle,
  AlertTriangle,
  Key,
  Mail,
  Slack,
  Webhook
} from 'lucide-react';

export function AdminSettings() {
  const [activeTab, setActiveTab] = useState('general');

  return (
    <AdminPageLayout
      title="Configuración del Sistema"
      subtitle="Ajustes generales, integraciones y configuración de seguridad"
      actions={
        <Button>
          <Save className="w-4 h-4 mr-2" />
          Guardar Cambios
        </Button>
      }
    >
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="integrations">Integraciones</TabsTrigger>
          <TabsTrigger value="notifications">Notificaciones</TabsTrigger>
          <TabsTrigger value="security">Seguridad</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general" className="mt-6">
          <div className="grid gap-6">
            {/* Configuración general */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Configuración General
                </CardTitle>
                <CardDescription>
                  Ajustes básicos del sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Nombre de la Empresa</Label>
                    <Input id="company-name" defaultValue="TendencIA Labs" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-email">Email del Administrador</Label>
                    <Input id="admin-email" type="email" defaultValue="admin@tendencia.ai" />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="timezone">Zona Horaria</Label>
                    <Select defaultValue="america/mexico_city">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="america/mexico_city">América/Ciudad de México</SelectItem>
                        <SelectItem value="america/new_york">América/Nueva York</SelectItem>
                        <SelectItem value="europe/madrid">Europa/Madrid</SelectItem>
                        <SelectItem value="utc">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currency">Moneda</Label>
                    <Select defaultValue="usd">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="usd">USD - Dólar Estadounidense</SelectItem>
                        <SelectItem value="mxn">MXN - Peso Mexicano</SelectItem>
                        <SelectItem value="eur">EUR - Euro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descripción de la Organización</Label>
                  <Textarea 
                    id="description" 
                    placeholder="Describe tu organización y el uso del sistema..."
                    defaultValue="Centro de control de costos de IA para optimización empresarial"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Modo de Desarrollo</h4>
                    <p className="text-sm text-muted-foreground">
                      Habilita logs detallados y herramientas de debugging
                    </p>
                  </div>
                  <Switch defaultChecked={false} />
                </div>
              </CardContent>
            </Card>

            {/* Configuración de límites */}
            <Card>
              <CardHeader>
                <CardTitle>Límites y Umbrales</CardTitle>
                <CardDescription>
                  Configuración de alertas y límites del sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cost-limit">Límite de Costo Mensual ($)</Label>
                    <Input id="cost-limit" type="number" defaultValue="50000" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="warning-threshold">Umbral de Advertencia (%)</Label>
                    <Input id="warning-threshold" type="number" defaultValue="80" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max-users">Máximo de Usuarios</Label>
                    <Input id="max-users" type="number" defaultValue="100" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="integrations" className="mt-6">
          <div className="grid gap-6">
            {/* APIs y servicios */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  APIs y Servicios Externos
                </CardTitle>
                <CardDescription>
                  Configuración de integraciones con servicios de IA
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* OpenAI */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Key className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">OpenAI API</h4>
                        <p className="text-sm text-muted-foreground">GPT-4, GPT-3.5, Embeddings</p>
                      </div>
                    </div>
                    <Badge className="text-green-600 bg-green-50">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Conectado
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="openai-key">API Key</Label>
                      <Input 
                        id="openai-key" 
                        type="password" 
                        placeholder="sk-..."
                        defaultValue="sk-••••••••••••••••••••••••••••••••••••••••••••••••"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="openai-org">Organization ID</Label>
                        <Input id="openai-org" placeholder="org-..." />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="openai-limit">Límite Mensual ($)</Label>
                        <Input id="openai-limit" type="number" defaultValue="10000" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Claude */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Key className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Anthropic Claude</h4>
                        <p className="text-sm text-muted-foreground">Claude 3, Claude 2</p>
                      </div>
                    </div>
                    <Badge className="text-green-600 bg-green-50">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Conectado
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="claude-key">API Key</Label>
                      <Input 
                        id="claude-key" 
                        type="password" 
                        placeholder="sk-ant-..."
                        defaultValue="sk-ant-••••••••••••••••••••••••••••••••••••••••••••••••"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="claude-limit">Límite Mensual ($)</Label>
                      <Input id="claude-limit" type="number" defaultValue="5000" />
                    </div>
                  </div>
                </div>

                {/* Supabase */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Shield className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Supabase</h4>
                        <p className="text-sm text-muted-foreground">Base de datos y autenticación</p>
                      </div>
                    </div>
                    <Badge className="text-green-600 bg-green-50">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Conectado
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="supabase-url">Project URL</Label>
                      <Input 
                        id="supabase-url" 
                        defaultValue="https://kounnglwsathqhxyxrwm.supabase.co"
                        readOnly
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="supabase-key">Anon Key</Label>
                      <Input 
                        id="supabase-key" 
                        type="password" 
                        defaultValue="eyJ••••••••••••••••••••••••••••••••••••••••••••••••"
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="notifications" className="mt-6">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Configuración de Notificaciones
                </CardTitle>
                <CardDescription>
                  Canales y reglas de notificación para alertas del sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Email */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Mail className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Email</h4>
                        <p className="text-sm text-muted-foreground">Notificaciones por correo electrónico</p>
                      </div>
                    </div>
                    <Switch defaultChecked={true} />
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="email-recipients">Destinatarios</Label>
                      <Input 
                        id="email-recipients" 
                        placeholder="admin@empresa.com, manager@empresa.com"
                        defaultValue="admin@tendencia.ai, alerts@tendencia.ai"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex items-center space-x-2">
                        <Switch id="email-critical" defaultChecked={true} />
                        <Label htmlFor="email-critical" className="text-sm">Críticas</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch id="email-warning" defaultChecked={true} />
                        <Label htmlFor="email-warning" className="text-sm">Advertencias</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch id="email-info" defaultChecked={false} />
                        <Label htmlFor="email-info" className="text-sm">Informativos</Label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Slack */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Slack className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Slack</h4>
                        <p className="text-sm text-muted-foreground">Notificaciones en Slack</p>
                      </div>
                    </div>
                    <Switch defaultChecked={false} />
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="slack-webhook">Webhook URL</Label>
                      <Input 
                        id="slack-webhook" 
                        placeholder="https://hooks.slack.com/services/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="slack-channel">Canal</Label>
                      <Input 
                        id="slack-channel" 
                        placeholder="#alerts"
                        defaultValue="#ai-cost-alerts"
                      />
                    </div>
                  </div>
                </div>

                {/* Webhooks */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Webhook className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Webhooks</h4>
                        <p className="text-sm text-muted-foreground">Notificaciones HTTP personalizadas</p>
                      </div>
                    </div>
                    <Switch defaultChecked={false} />
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="webhook-url">URL del Webhook</Label>
                      <Input 
                        id="webhook-url" 
                        placeholder="https://api.empresa.com/webhooks/alerts"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="webhook-secret">Secret (opcional)</Label>
                      <Input 
                        id="webhook-secret" 
                        type="password"
                        placeholder="Para verificación de firma"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="security" className="mt-6">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Configuración de Seguridad
                </CardTitle>
                <CardDescription>
                  Autenticación, autorización y políticas de seguridad
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Autenticación */}
                <div className="space-y-4">
                  <h4 className="font-medium">Autenticación</h4>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <h5 className="font-medium">Autenticación de Dos Factores (2FA)</h5>
                      <p className="text-sm text-muted-foreground">Requerir 2FA para todos los administradores</p>
                    </div>
                    <Switch defaultChecked={true} />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <h5 className="font-medium">SSO (Single Sign-On)</h5>
                      <p className="text-sm text-muted-foreground">Permitir autenticación a través de proveedores externos</p>
                    </div>
                    <Switch defaultChecked={false} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="session-timeout">Timeout de Sesión (minutos)</Label>
                      <Input id="session-timeout" type="number" defaultValue="480" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-attempts">Máximos Intentos de Login</Label>
                      <Input id="max-attempts" type="number" defaultValue="5" />
                    </div>
                  </div>
                </div>

                {/* Políticas de contraseña */}
                <div className="space-y-4">
                  <h4 className="font-medium">Políticas de Contraseña</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="min-length">Longitud Mínima</Label>
                      <Input id="min-length" type="number" defaultValue="8" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expiry-days">Expiración (días)</Label>
                      <Input id="expiry-days" type="number" defaultValue="90" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Switch id="require-uppercase" defaultChecked={true} />
                      <Label htmlFor="require-uppercase" className="text-sm">Requerir mayúsculas</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="require-numbers" defaultChecked={true} />
                      <Label htmlFor="require-numbers" className="text-sm">Requerir números</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="require-symbols" defaultChecked={true} />
                      <Label htmlFor="require-symbols" className="text-sm">Requerir símbolos especiales</Label>
                    </div>
                  </div>
                </div>

                {/* Logs y auditoría */}
                <div className="space-y-4">
                  <h4 className="font-medium">Logs y Auditoría</h4>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <h5 className="font-medium">Log de Accesos</h5>
                      <p className="text-sm text-muted-foreground">Registrar todos los accesos al sistema</p>
                    </div>
                    <Switch defaultChecked={true} />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <h5 className="font-medium">Log de Acciones</h5>
                      <p className="text-sm text-muted-foreground">Registrar cambios en configuración y datos</p>
                    </div>
                    <Switch defaultChecked={true} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="log-retention">Retención de Logs (días)</Label>
                    <Input id="log-retention" type="number" defaultValue="365" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </AdminPageLayout>
  );
}